import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import deepEqual from '../utils/deepEqual';
import { getCurrencySymbol } from '../utils/currencySymbols';
import { formatMoney, normalizeCurrencyCode } from '../utils/money';
import { normaliseKioskTheme } from '../utils/themeColour';

const KioskThemeContext = createContext({
    themeOverrides: null,
    setThemeOverrides: () => { },
    currencyCode: null,
    currencySymbol: '$',
    setCurrency: () => { },
    formatMoney: (amount, options) => formatMoney(amount, null, options),
    filterSettings: null,
    setFilterSettings: () => { },
});

export function KioskThemeProvider({ children }) {
    // TKT-2026-107229: the cached theme is what the kiosk renders before (and,
    // offline, instead of) the store config. A colour MUI cannot parse in it kept
    // the device blank, so its colours are normalised here as they are loaded and
    // again in setThemeOverrides before they are held or cached.
    const [themeOverrides, setThemeOverridesRaw] = useState(() => {
        try {
            const stored = localStorage.getItem('kiosk-themeOverrides');
            const parsed = stored ? JSON.parse(stored) : null;
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
                ? normaliseKioskTheme(parsed)
                : null;
        } catch {
            return null;
        }
    });

    // TKT-2026-106987: the store's currency CODE, not just its symbol. The symbol
    // alone cannot say how many decimals a price has: "¥" is both yen (none) and
    // yuan (two). Persisted so an already-unlocked kiosk formats correctly before
    // the next display-config fetch lands.
    const [currencyCode, setCurrencyCodeRaw] = useState(() => {
        try {
            return normalizeCurrencyCode(localStorage.getItem('kiosk-currencyCode'));
        } catch {
            return null;
        }
    });

    // TKT-2026-107434: the store's sign as the display config sent it (sortswift-api's
    // list), persisted with the code so a kiosk shows it before the next config
    // fetch. A kiosk unlocked before the code was persisted keeps its stored sign
    // until the config refetch.
    const [storedSymbol, setStoredSymbol] = useState(() => {
        try {
            return localStorage.getItem('kiosk-currencySymbol') || null;
        } catch {
            return null;
        }
    });

    const currencySymbol = storedSymbol || (currencyCode ? getCurrencySymbol(currencyCode) : '$');

    // Owner-defined filter enable/disable map (TKT-2026-102569). Persisted like the
    // theme so the kiosk reflects the owner's choices even on an already-unlocked
    // session before the next theme refresh lands.
    const [filterSettings, setFilterSettingsRaw] = useState(() => {
        try {
            const stored = localStorage.getItem('kiosk-filterSettings');
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    });

    // The store's theme is refetched on every mount of the home screen, and comes
    // back unchanged virtually every time. Writing the new object anyway used to
    // rebuild the whole MUI theme in App (`createTheme` is keyed on this value),
    // re-rendering and restyling the entire kiosk for no reason. Keep the existing
    // object when nothing actually changed. TKT-2026-105056
    const setThemeOverrides = useCallback((rawOverrides) => {
        const overrides = normaliseKioskTheme(rawOverrides);
        setThemeOverridesRaw((prev) => (deepEqual(prev, overrides) ? prev : overrides));
        if (overrides) {
            localStorage.setItem('kiosk-themeOverrides', JSON.stringify(overrides));
        } else {
            localStorage.removeItem('kiosk-themeOverrides');
        }
    }, []);

    // The store's currency code and sign, as the display config sends them ("SEK",
    // "kr"). An older backend sends no sign: the kiosk draws one from the code.
    const setCurrency = useCallback((code, configSymbol) => {
        const normalized = normalizeCurrencyCode(code);
        setCurrencyCodeRaw(normalized);
        let symbol = '$';
        if (normalized) {
            symbol = typeof configSymbol === 'string' && configSymbol.trim()
                ? configSymbol.trim()
                : getCurrencySymbol(normalized);
        }
        setStoredSymbol(symbol);
        try {
            if (normalized) {
                localStorage.setItem('kiosk-currencyCode', normalized);
            } else {
                localStorage.removeItem('kiosk-currencyCode');
            }
            localStorage.setItem('kiosk-currencySymbol', symbol);
        } catch {
            // Storage full or blocked: the in-memory currency still applies.
        }
    }, []);

    // Every amount on screen goes through this: the store's symbol, its digits,
    // grouped thousands.
    const boundFormatMoney = useCallback(
        (amount, options = {}) => formatMoney(amount, currencyCode, { symbol: currencySymbol, ...options }),
        [currencyCode, currencySymbol],
    );

    const setFilterSettings = useCallback((settings) => {
        setFilterSettingsRaw((prev) => (deepEqual(prev, settings) ? prev : settings));
        if (settings && typeof settings === 'object') {
            localStorage.setItem('kiosk-filterSettings', JSON.stringify(settings));
        } else {
            localStorage.removeItem('kiosk-filterSettings');
        }
    }, []);

    const value = useMemo(() => ({
        themeOverrides,
        setThemeOverrides,
        currencyCode,
        currencySymbol,
        setCurrency,
        formatMoney: boundFormatMoney,
        filterSettings,
        setFilterSettings,
    }), [themeOverrides, setThemeOverrides, currencyCode, currencySymbol, setCurrency, boundFormatMoney, filterSettings, setFilterSettings]);

    return (
        <KioskThemeContext.Provider value={value}>
            {children}
        </KioskThemeContext.Provider>
    );
}

export function useKioskTheme() {
    return useContext(KioskThemeContext);
}

export default KioskThemeContext;
